import {
  TUTORIAL_MISSIONS,
  type TutorialMission,
  type TutorialMissionKey,
} from "@/constants/tutorialMissions";

/** API (GET /api/tutorial-missions) が返す 1 件分 */
export interface TutorialMissionStatusDto {
  key: string;
  clearedAt: string | null;
  rewardPoint: number | null;
}

export interface TutorialMissionView {
  mission: TutorialMission;
  cleared: boolean;
  clearedAt: string | null;
  /** もらった (クリア済み) か、もらえる (未クリア) ポイント */
  rewardPoint: number;
}

export interface TutorialProgress {
  views: TutorialMissionView[];
  clearedCount: number;
  total: number;
  earnedPoint: number;
  totalPoint: number;
  /** 次にやるとよいミッション (未クリアの操作のミッションのうち最初のもの)。全部済みなら null */
  next: TutorialMission | null;
}

/** 定義の順に、クリア状況を重ねる (API に無いミッションは未クリア扱い) */
export function buildTutorialProgress(
  statuses: readonly TutorialMissionStatusDto[],
): TutorialProgress {
  const byKey = new Map(statuses.map((s) => [s.key, s]));
  const views = TUTORIAL_MISSIONS.map((mission): TutorialMissionView => {
    const status = byKey.get(mission.key);
    const cleared = Boolean(status?.clearedAt);
    return {
      mission,
      cleared,
      clearedAt: cleared ? (status?.clearedAt ?? null) : null,
      rewardPoint: cleared ? (status?.rewardPoint ?? mission.reward) : mission.reward,
    };
  });
  const clearedViews = views.filter((v) => v.cleared);
  return {
    views,
    clearedCount: clearedViews.length,
    total: views.length,
    earnedPoint: clearedViews.reduce((sum, v) => sum + v.rewardPoint, 0),
    totalPoint: views.reduce((sum, v) => sum + v.mission.reward, 0),
    next:
      views.find((v) => !v.cleared && v.mission.clear === "action")?.mission ?? null,
  };
}

/** クリア済みのキーの集合 */
export function clearedKeys(
  statuses: readonly TutorialMissionStatusDto[],
): Set<TutorialMissionKey> {
  const keys = new Set<TutorialMissionKey>();
  for (const s of statuses) {
    const mission = TUTORIAL_MISSIONS.find((m) => m.key === s.key);
    if (mission && s.clearedAt) keys.add(mission.key);
  }
  return keys;
}

/** クリアした結果をキャッシュに反映する (再取得を待たずに一覧を更新するため) */
export function applyClear(
  statuses: readonly TutorialMissionStatusDto[],
  clear: { key: string; clearedAt: string; rewardPoint: number },
): TutorialMissionStatusDto[] {
  const exists = statuses.some((s) => s.key === clear.key);
  const next = statuses.map((s) =>
    s.key === clear.key
      ? { key: s.key, clearedAt: clear.clearedAt, rewardPoint: clear.rewardPoint }
      : s,
  );
  return exists
    ? next
    : [...next, { key: clear.key, clearedAt: clear.clearedAt, rewardPoint: clear.rewardPoint }];
}
